// Client for reviewing the ticket agent's update_template recommendation:
// guardrail check → draft version → Test Note runs → save.
// Backed by server/routes/templateEdits.js.

import { environment } from "../../config/environment";
import { customFetch } from "../../utils/Auth";
import { fetchTestNoteResult, startTestNoteRun } from "./testNoteApi";

const TEMPLATE_EDITS_URL = `${environment.apiUrl}/support/template-edits`;

const TEST_NOTE_POLL_INTERVAL_MS = 5000;
const TEST_NOTE_POLL_TIMEOUT_MS = 5 * 60 * 1000;

const postJson = async (path, body) => {
  const response = await customFetch(`${TEMPLATE_EDITS_URL}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  return { status: response.status, data };
};

/**
 * Only a 200 with a boolean `allowed` is an answer; anything else is a failed
 * lookup, never a denial.
 * @returns {Promise<{allowed: boolean} | {error: string}>}
 */
export async function fetchTemplateEditAccess() {
  try {
    const response = await customFetch(`${TEMPLATE_EDITS_URL}/access`);
    const data = await response.json().catch(() => null);
    if (!response.ok || typeof data?.allowed !== "boolean") {
      return {
        error: `Could not check template edit access (HTTP ${response.status}).`,
      };
    }
    return { allowed: data.allowed };
  } catch {
    return { error: "Could not reach the server to check template edit access." };
  }
}

/**
 * Only a 200 with a boolean `accepted` is a decision; anything else means no
 * decision was made and must never be treated as accepted.
 * @returns {Promise<{decision: {accepted: boolean, reason: string, violations: object[]}} | {error: string}>}
 */
export async function checkTemplateGuardrails({
  name,
  version,
  newText,
  requestedChange,
}) {
  try {
    const { status, data } = await postJson("/validate", {
      name,
      version,
      new_text: newText,
      ...(requestedChange ? { requested_change: requestedChange } : {}),
    });
    if (status !== 200 || typeof data?.accepted !== "boolean") {
      return {
        error:
          data?.error ||
          data?.message ||
          `The guardrail check did not return a decision (HTTP ${status}).`,
      };
    }
    return {
      decision: {
        accepted: data.accepted === true,
        reason: data.reason || "",
        violations: Array.isArray(data.violations) ? data.violations : [],
      },
    };
  } catch {
    return { error: "Could not reach the guardrail check." };
  }
}

/** Creates a non-default version holding `text`. @returns {{draft: {name, version}} | {error}} */
export async function createTemplateDraft({ name, baseVersion, text }) {
  try {
    const { status, data } = await postJson("/draft", {
      name,
      base_version: baseVersion,
      text,
    });
    if (status !== 200 || !data?.version) {
      return {
        error: [data?.error, data?.details].filter(Boolean).join(": ") ||
          "Failed to create the draft version.",
      };
    }
    return { draft: { name: data.name, version: data.version } };
  } catch {
    return { error: "Failed to create the draft version." };
  }
}

/**
 * Saves the tested draft as a new version of the prompt and makes it the
 * default.
 * @returns {Promise<{saved: true, version: string, previousVersion: string} | {error: string}>}
 */
export async function saveTemplateEdit({ name, draftVersion, baseVersion }) {
  try {
    const { status, data } = await postJson("/save", {
      name,
      draft_version: draftVersion,
      base_version: baseVersion,
    });
    if (status !== 200 || !data?.saved) {
      return { error: data?.error || "Failed to save the template." };
    }
    return {
      saved: true,
      version: data.version,
      previousVersion: data.previous_version,
    };
  } catch {
    return { error: "Failed to save the template." };
  }
}

/**
 * Runs Test Note for the patient with the conversation prompt pinned to
 * name+version and waits for the generated note.
 * @param {() => boolean} isActive Polling stops once this returns false.
 * @returns {Promise<{note: string, jobName: string, generationError: string} | {error: string} | {abandoned: true}>}
 */
export async function runTestNoteWithPrompt({ patientId, name, version, isActive }) {
  const started = await startTestNoteRun({
    patientId,
    convPromptName: name,
    convPromptVersion: version,
  });
  if (!started.success) return { error: started.error };

  const jobName = started.run.test_summarize_job_name;
  const deadline = Date.now() + TEST_NOTE_POLL_TIMEOUT_MS;
  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, TEST_NOTE_POLL_INTERVAL_MS));
    if (!isActive()) return { abandoned: true };

    const result = await fetchTestNoteResult(jobName);
    if (result.error) return { error: result.error };
    if (result.ready) {
      const note = result.job?.soap_note || "";
      const generationError = result.job?.summarize_error || "";
      if (!note.trim()) {
        return {
          error:
            generationError ||
            "The pipeline finished but produced no note. Check the lambda logs.",
        };
      }
      return { note, jobName, generationError };
    }
  }
  return {
    error:
      "Timed out waiting for the test note. The run may still finish — check the eval summarize table.",
  };
}
